import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const supportsWebGL = () => {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch {
    return false;
  }
};

const StaticFallback = () => (
  <div className="hero-scene-fallback" aria-label="Layered signage preview">
    <div className="fallback-panel fallback-panel-main">
      <span>UV FLEX</span>
      <strong>1440 DPI</strong>
    </div>
    <div className="fallback-panel fallback-panel-red">
      <span>VINYL</span>
      <strong>WRAPS</strong>
    </div>
    <div className="fallback-panel fallback-panel-dark">
      <span>LIVE</span>
      <strong>QUOTE</strong>
    </div>
  </div>
);

const createPanel = ({ width, height, depth, color, x, y, z, labelColor = '#0A0A0A' }) => {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(width, height, depth),
    new THREE.MeshStandardMaterial({
      color,
      roughness: 0.58,
      metalness: 0.08,
    })
  );
  const border = new THREE.LineSegments(
    new THREE.EdgesGeometry(body.geometry),
    new THREE.LineBasicMaterial({ color: labelColor, linewidth: 2 })
  );
  group.add(body, border);
  group.position.set(x, y, z);
  return group;
};

export default function HeroScene() {
  const mountRef = useRef(null);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !supportsWebGL()) {
      setFallback(true);
      return undefined;
    }

    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#F9F9F6');

    const camera = new THREE.PerspectiveCamera(34, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 0.1, 8.5);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight('#ffffff', 1.8);
    const keyLight = new THREE.DirectionalLight('#ffffff', 2.2);
    keyLight.position.set(3, 5, 4);
    const redLight = new THREE.PointLight('#FF2A00', 36, 9);
    redLight.position.set(-2.5, -1, 3);
    scene.add(ambientLight, keyLight, redLight);

    const rig = new THREE.Group();
    scene.add(rig);

    const panels = [
      createPanel({ width: 4.2, height: 2.2, depth: 0.12, color: '#FFFFFF', x: -0.65, y: 0.75, z: 0, labelColor: '#0A0A0A' }),
      createPanel({ width: 3.3, height: 1.65, depth: 0.14, color: '#FF2A00', x: 1.1, y: -0.35, z: 0.55, labelColor: '#FFFFFF' }),
      createPanel({ width: 2.6, height: 1.25, depth: 0.16, color: '#0A0A0A', x: -1.25, y: -1.05, z: 1.1, labelColor: '#FF2A00' }),
    ];
    panels.forEach((panel, index) => {
      panel.rotation.set(THREE.MathUtils.degToRad(index === 1 ? -6 : 5), THREE.MathUtils.degToRad(index === 2 ? -12 : 10), THREE.MathUtils.degToRad(index === 1 ? 4 : -3));
      rig.add(panel);
    });

    const rails = new THREE.Group();
    const railMaterial = new THREE.MeshStandardMaterial({ color: '#0A0A0A', roughness: 0.44, metalness: 0.18 });
    for (let i = 0; i < 8; i += 1) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(0.045, 4.8, 0.045), railMaterial);
      rail.position.set(-2.5 + i * 0.72, -0.1, -0.75);
      rail.rotation.z = THREE.MathUtils.degToRad(90);
      rails.add(rail);
    }
    rig.add(rails);

    const inkDots = new THREE.Group();
    const dotMaterial = new THREE.MeshStandardMaterial({ color: '#FF2A00', roughness: 0.4, metalness: 0 });
    for (let i = 0; i < 26; i += 1) {
      const dot = new THREE.Mesh(new THREE.SphereGeometry(0.035 + (i % 3) * 0.012, 16, 16), dotMaterial);
      dot.position.set(-2.9 + Math.random() * 5.8, -1.85 + Math.random() * 3.4, 1.4 + Math.random() * 0.8);
      inkDots.add(dot);
    }
    rig.add(inkDots);

    const cursor = { x: 0, y: 0 };
    const handlePointerMove = (event) => {
      const rect = mount.getBoundingClientRect();
      cursor.x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      cursor.y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    const handleResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };

    mount.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('resize', handleResize);

    let rafId;
    const clock = new THREE.Clock();
    const animate = () => {
      const t = clock.getElapsedTime();
      rig.rotation.y = Math.sin(t * 0.42) * 0.08 + cursor.x * 0.08;
      rig.rotation.x = Math.sin(t * 0.32) * 0.035 - cursor.y * 0.035;
      panels.forEach((panel, index) => {
        panel.position.z += Math.sin(t * 1.15 + index) * 0.0018;
      });
      inkDots.rotation.z = t * 0.08;
      redLight.intensity = 28 + Math.sin(t * 1.4) * 7;
      renderer.render(scene, camera);
      rafId = window.requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.cancelAnimationFrame(rafId);
      mount.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose());
          else object.material.dispose();
        }
      });
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div className="hero-scene-shell" data-testid="hero-3d-scene">
      <div className="hero-scene-label">3D print control preview</div>
      {fallback ? <StaticFallback /> : <div ref={mountRef} className="hero-scene-canvas" aria-hidden="true" />}
      <div className="hero-scene-readout">
        <span>Material stack</span>
        <strong>Flex / Vinyl / Frame</strong>
      </div>
    </div>
  );
}
